import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  Pencil,
  RefreshCw,
  Eye,
  EyeOff,
  Star,
  ShieldCheck,
  ShieldAlert,
  ExternalLink,
  CheckCircle2,
  Tag,
  CheckCheck,
  XCircle,
  Clock,
  RefreshCcw,
  AlertOctagon,
  Sparkles,
  Hourglass,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import ProposalPanel from './EnergyControlProposalPanel';

const API = '/api';

const CATEGORY_LABELS = {
  measure_first: 'Eerst meten',
  manual_start: 'Handmatig starten',
  auto_switching: 'Automatisch schakelen',
  installer: 'Installateur',
};

const CATEGORY_ORDER = ['measure_first', 'manual_start', 'auto_switching', 'installer'];

const STATUS_META = {
  pending_review: { label: 'Pending review', cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: Hourglass },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCheck },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300', icon: XCircle },
  later: { label: 'Later', cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  needs_update: { label: 'Needs update', cls: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertOctagon },
};

const PRIORITY_META = {
  high: { label: 'High', cls: 'bg-rose-50 text-rose-700' },
  medium: { label: 'Medium', cls: 'bg-amber-50 text-amber-700' },
  low: { label: 'Low', cls: 'bg-slate-50 text-slate-600' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.approved;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function UrlBadge({ url, valid, label }) {
  if (!url) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
        title={`Geen ${label} ingesteld — UI toont safe-fallback "Vraag advies"`}
      >
        — geen {label}
      </span>
    );
  }
  if (valid) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors"
        title={url}
      >
        <ShieldCheck className="w-3 h-3" />
        {label} OK
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-800"
      title={`Ongeldige ${label} (placeholder/preview/relative)`}
    >
      <ShieldAlert className="w-3 h-3" />
      {label} ongeldig
    </span>
  );
}

function ApprovalActions({ product, busy, onPatch }) {
  const status = product.status || 'approved';
  const baseBtn =
    'inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border transition-colors disabled:opacity-50';
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`ec-product-approval-actions-${product.product_id}`}>
      <button
        type="button"
        disabled={busy || status === 'approved'}
        onClick={() => onPatch({ status: 'approved' })}
        className={`${baseBtn} border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100`}
        data-testid={`ec-product-approve-${product.product_id}`}
      >
        <CheckCheck className="w-3.5 h-3.5" />
        Goedkeuren
      </button>
      <button
        type="button"
        disabled={busy || status === 'rejected'}
        onClick={() => onPatch({ status: 'rejected', visible: false })}
        className={`${baseBtn} border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100`}
        data-testid={`ec-product-reject-${product.product_id}`}
      >
        <XCircle className="w-3.5 h-3.5" />
        Afwijzen
      </button>
      <button
        type="button"
        disabled={busy || status === 'later'}
        onClick={() => onPatch({ status: 'later', visible: false })}
        className={`${baseBtn} border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100`}
        data-testid={`ec-product-later-${product.product_id}`}
      >
        <Clock className="w-3.5 h-3.5" />
        Later
      </button>
      <button
        type="button"
        disabled={busy || status === 'needs_update'}
        onClick={() => onPatch({ status: 'needs_update' })}
        className={`${baseBtn} border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100`}
        data-testid={`ec-product-needs-update-${product.product_id}`}
      >
        <AlertOctagon className="w-3.5 h-3.5" />
        Update nodig
      </button>
    </div>
  );
}

function ProductRow({ product, onEdit, onPatch, onRefresh, token, busy }) {
  const {
    product_id,
    name,
    reason_short,
    price_indication,
    product_url,
    affiliate_url,
    is_valid_product_url,
    is_valid_affiliate_url,
    effective_url,
    visible,
    featured,
    tags = [],
    admin_modified,
    status,
    priority,
    role,
    note,
    safety_note,
    is_publishable,
  } = product;
  const isPending = status === 'pending_review';
  const isLive = is_publishable;

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        isPending ? 'bg-amber-50/40 border-amber-200' :
        status === 'rejected' ? 'bg-muted/50 border-rose-200 opacity-75' :
        status === 'later' ? 'bg-muted/30 border-slate-200 opacity-80' :
        status === 'needs_update' ? 'bg-orange-50/40 border-orange-200' :
        !visible ? 'bg-muted/40 opacity-75' :
        'bg-white border-border'
      }`}
      data-testid={`ec-product-row-${product_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-foreground" data-testid={`ec-product-name-${product_id}`}>
              {name}
            </h3>
            <StatusBadge status={status} />
            {priority && PRIORITY_META[priority] && (
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${PRIORITY_META[priority].cls}`}>
                {PRIORITY_META[priority].label}
              </span>
            )}
            {isLive && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white"
                title="Approved + zichtbaar — wordt aan klanten getoond"
                data-testid={`ec-product-live-${product_id}`}
              >
                <Sparkles className="w-3 h-3" /> LIVE
              </span>
            )}
            {featured && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                <Star className="w-3 h-3 fill-amber-600 text-amber-600" /> Featured
              </span>
            )}
            {!visible && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">
                <EyeOff className="w-3 h-3" /> Verborgen
              </span>
            )}
            {admin_modified && (
              <span
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800"
                title="Door admin gewijzigd"
              >
                Gewijzigd
              </span>
            )}
          </div>
          {role && (
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1 font-medium">
              Rol: {role}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{reason_short}</p>
          <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">
            <code>{product_id}</code> · {price_indication || '—'}
          </div>
          {note && (
            <div className="mt-2 text-[11px] text-amber-900 bg-amber-100/50 rounded px-2 py-1 border border-amber-200">
              <strong>Notitie:</strong> {note}
            </div>
          )}
          {safety_note && (
            <div className="mt-1 text-[11px] text-rose-900 bg-rose-50 rounded px-2 py-1 border border-rose-200">
              <ShieldAlert className="inline w-3 h-3 mr-1" /> {safety_note}
            </div>
          )}
          {status === 'needs_update' && isLive && (
            <div
              className="mt-2 text-[11px] text-orange-900 bg-orange-100/60 rounded px-2 py-1 border border-orange-300"
              data-testid={`ec-product-needs-update-warning-${product_id}`}
            >
              <AlertOctagon className="inline w-3 h-3 mr-1" />
              Dit product is publiek zichtbaar maar gemarkeerd als <strong>needs_update</strong> — controleer prijs/link.
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 mt-2">
            <UrlBadge url={product_url} valid={is_valid_product_url} label="product-URL" />
            <UrlBadge url={affiliate_url} valid={is_valid_affiliate_url} label="affiliate" />
            {effective_url && (
              <span
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                title="URL die de klant ziet (affiliate wint)"
              >
                <CheckCircle2 className="w-3 h-3" /> klant ziet
              </span>
            )}
            {tags.length > 0 && tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800"
              >
                <Tag className="w-3 h-3" />
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 items-end">
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              disabled={busy}
              onClick={() => onPatch({ visible: !visible })}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-lg border border-border hover:bg-muted disabled:opacity-50"
              title={visible ? 'Verberg voor klanten' : 'Maak zichtbaar'}
              data-testid={`ec-product-toggle-visible-${product_id}`}
            >
              {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {visible ? 'Zichtbaar' : 'Verborgen'}
            </button>
            <button
              type="button"
              disabled={busy || (isPending && !featured && status !== 'approved')}
              onClick={() => onPatch({ featured: !featured })}
              className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-lg border disabled:opacity-50 ${
                featured ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-border hover:bg-muted'
              }`}
              title={
                isPending
                  ? 'Featuren pas mogelijk na goedkeuren'
                  : featured
                    ? 'Niet meer uitlichten'
                    : 'Lichtuitgelicht (Top match)'
              }
              data-testid={`ec-product-toggle-featured-${product_id}`}
            >
              <Star className={`w-3.5 h-3.5 ${featured ? 'fill-amber-500 text-amber-500' : ''}`} />
              {featured ? 'Featured' : 'Uitlichten'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onEdit(product)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
              data-testid={`ec-product-edit-${product_id}`}
            >
              <Pencil className="w-3.5 h-3.5" /> Bewerk
            </button>
          </div>
          <ApprovalActions product={product} busy={busy} onPatch={onPatch} />
        </div>
      </div>
      <ProposalPanel
        product={product}
        token={token}
        onApplied={() => onRefresh?.()}
        onRejected={() => onRefresh?.()}
      />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">
        {label}
        {required && <span className="text-rose-600 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function ProductEditModal({ product, open, onClose, onSaved, token }) {
  const [form, setForm] = useState(null);
  const [tagsInput, setTagsInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        reason_short: product.reason_short || '',
        audience: product.audience || '',
        price_indication: product.price_indication || '',
        compat_note: product.compat_note || '',
        inquiry_cta: product.inquiry_cta || '',
        product_url: product.product_url || '',
        affiliate_url: product.affiliate_url || '',
        sort_order: product.sort_order ?? 99,
        visible: product.visible !== false,
        featured: !!product.featured,
        status: product.status || 'approved',
        priority: product.priority || 'medium',
        note: product.note || '',
        safety_note: product.safety_note || '',
        role: product.role || '',
      });
      setTagsInput((product.tags || []).join(', '));
    }
  }, [product]);

  if (!product || !form) return null;

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      // Enforce policy: pending_review products can never be featured.
      let featuredOut = !!form.featured;
      if (form.status === 'pending_review' && featuredOut) {
        featuredOut = false;
        toast.warning('Featured uitgezet: pending_review producten mogen nog niet uitgelicht worden.');
      }
      const payload = {
        name: form.name.trim(),
        reason_short: form.reason_short.trim(),
        audience: form.audience.trim(),
        price_indication: form.price_indication.trim(),
        compat_note: form.compat_note.trim(),
        inquiry_cta: form.inquiry_cta.trim(),
        product_url: form.product_url.trim(),
        affiliate_url: form.affiliate_url.trim(),
        sort_order: parseInt(form.sort_order, 10) || 99,
        visible: !!form.visible,
        featured: featuredOut,
        tags,
        status: form.status,
        priority: form.priority,
        note: form.note.trim(),
        safety_note: form.safety_note.trim(),
        role: form.role.trim(),
      };
      const res = await axios.patch(
        `${API}/admin/energy-control/products/${product.product_id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const coerced = res.data?.coerced_fields || [];
      if (coerced.length > 0) {
        coerced.forEach((c) => {
          toast.warning(`URL was ongeldig en is teruggezet: ${c.field}`, {
            description: c.submitted,
          });
        });
      } else {
        toast.success('Product bijgewerkt');
      }
      onSaved(res.data?.product);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Onbekende fout';
      toast.error(`Opslaan mislukt: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ec-product-edit-modal">
        <DialogHeader>
          <DialogTitle>Product bewerken</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            ID: {product.product_id} · {CATEGORY_LABELS[product.category] || product.category}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Approval workflow */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-emerald-900 font-semibold flex items-center gap-1.5">
              <CheckCheck className="w-3.5 h-3.5" /> Approval-status
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Status">
                <select
                  className="w-full px-3 py-2 border border-input rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={form.status}
                  onChange={(e) => update('status', e.target.value)}
                  data-testid="ec-product-edit-status"
                >
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Prioriteit">
                <select
                  className="w-full px-3 py-2 border border-input rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={form.priority}
                  onChange={(e) => update('priority', e.target.value)}
                  data-testid="ec-product-edit-priority"
                >
                  {Object.entries(PRIORITY_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Rol (intern label, bv. 'basis meetmodule')">
              <Input
                value={form.role}
                onChange={(e) => update('role', e.target.value)}
                data-testid="ec-product-edit-role"
              />
            </Field>
            <Field label="Admin notitie (intern)">
              <textarea
                rows={2}
                className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                value={form.note}
                onChange={(e) => update('note', e.target.value)}
                data-testid="ec-product-edit-note"
              />
            </Field>
            <Field label="Veiligheidsnotitie (zichtbaar voor admin)">
              <Input
                value={form.safety_note}
                onChange={(e) => update('safety_note', e.target.value)}
                data-testid="ec-product-edit-safety-note"
              />
            </Field>
          </div>

          <Field label="Naam" required>
            <Input
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              data-testid="ec-product-edit-name"
            />
          </Field>

          <Field label="Beschrijving (reason_short)">
            <textarea
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={form.reason_short}
              onChange={(e) => update('reason_short', e.target.value)}
              data-testid="ec-product-edit-reason"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Doelgroep (audience)">
              <Input
                value={form.audience}
                onChange={(e) => update('audience', e.target.value)}
                data-testid="ec-product-edit-audience"
              />
            </Field>
            <Field label="Richtprijs">
              <Input
                value={form.price_indication}
                onChange={(e) => update('price_indication', e.target.value)}
                data-testid="ec-product-edit-price"
              />
            </Field>
          </div>

          <Field label="Compat-notitie (klein vinkje onder kaart)">
            <Input
              value={form.compat_note}
              onChange={(e) => update('compat_note', e.target.value)}
              data-testid="ec-product-edit-compat"
            />
          </Field>

          <Field label="Inquiry CTA (knop-tekst voor advies-form)">
            <Input
              value={form.inquiry_cta}
              onChange={(e) => update('inquiry_cta', e.target.value)}
              data-testid="ec-product-edit-inquiry-cta"
            />
          </Field>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" /> URL's — strikte validatie
            </div>
            <p className="text-xs text-amber-900">
              Alleen externe HTTPS-links worden geaccepteerd. Placeholder-, preview-, relatieve- of
              <code> powersure-worldwide.com</code>-URL's worden automatisch teruggezet naar
              <em> safe-fallback</em> (klant ziet "Vraag advies over deze optie").
            </p>
            <Field label="Product-URL (retailer / fabrikant)">
              <Input
                value={form.product_url}
                onChange={(e) => update('product_url', e.target.value)}
                placeholder="https://www.homewizard.com/p1-meter"
                data-testid="ec-product-edit-product-url"
              />
            </Field>
            <Field label="Affiliate-URL (heeft voorrang als geldig)">
              <Input
                value={form.affiliate_url}
                onChange={(e) => update('affiliate_url', e.target.value)}
                placeholder="https://amzn.eu/d/..."
                data-testid="ec-product-edit-affiliate-url"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Sort order">
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => update('sort_order', e.target.value)}
                data-testid="ec-product-edit-sort-order"
              />
            </Field>
            <div className="flex items-end gap-2">
              <Switch
                checked={form.visible}
                onCheckedChange={(v) => update('visible', v)}
                data-testid="ec-product-edit-visible"
              />
              <span className="text-sm text-foreground">Zichtbaar</span>
            </div>
            <div className="flex items-end gap-2">
              <Switch
                checked={form.featured}
                onCheckedChange={(v) => update('featured', v)}
                disabled={form.status === 'pending_review'}
                data-testid="ec-product-edit-featured"
              />
              <span className="text-sm text-foreground">
                Featured
                {form.status === 'pending_review' && (
                  <span className="block text-[10px] text-muted-foreground">eerst goedkeuren</span>
                )}
              </span>
            </div>
          </div>

          <Field label="Tags (komma-gescheiden, bijv. promo, best_seller)">
            <Input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              data-testid="ec-product-edit-tags"
              placeholder="best_seller, promo"
            />
          </Field>
        </div>

        <DialogFooter className="mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted disabled:opacity-50"
            data-testid="ec-product-edit-cancel"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="ec-product-edit-save"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Opslaan
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CountCard({ label, value, testid, tone = 'default' }) {
  const toneCls = {
    default: 'bg-white border-border',
    emerald: 'bg-emerald-50 border-emerald-200',
    rose: 'bg-rose-50 border-rose-200',
    amber: 'bg-amber-50 border-amber-200',
    orange: 'bg-orange-50 border-orange-200',
    slate: 'bg-slate-50 border-slate-200',
  }[tone];
  return (
    <div className={`rounded-lg border ${toneCls} p-3`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold text-foreground mt-0.5">{value ?? '—'}</div>
    </div>
  );
}

const FILTER_DEFS = [
  { id: 'all', label: 'Alle' },
  { id: 'pending_review', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'live', label: 'Live (approved + zichtbaar)' },
  { id: 'needs_update', label: 'Needs update' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'later', label: 'Later' },
  { id: 'visible', label: 'Zichtbaar' },
  { id: 'hidden', label: 'Verborgen' },
  { id: 'featured', label: 'Featured' },
  { id: 'with_link', label: 'Met geldige link' },
  { id: 'no_link', label: 'Zonder klant-link' },
  { id: 'has_proposal', label: 'Heeft agent-voorstel' },
];

function applyFilter(products, filter) {
  switch (filter) {
    case 'pending_review':
    case 'approved':
    case 'rejected':
    case 'later':
    case 'needs_update':
      return products.filter((p) => p.status === filter);
    case 'live':
      return products.filter((p) => p.is_publishable);
    case 'visible':
      return products.filter((p) => p.visible !== false);
    case 'hidden':
      return products.filter((p) => p.visible === false);
    case 'featured':
      return products.filter((p) => p.featured);
    case 'has_proposal':
      return products.filter((p) => p.proposed_update && p.proposed_update.proposal_status === 'pending');
    case 'with_link':
      return products.filter((p) => !!p.effective_url);
    case 'no_link':
      return products.filter((p) => !p.effective_url);
    case 'all':
    default:
      return products;
  }
}

export default function EnergyControlProductsTab({ token }) {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyIds, setBusyIds] = useState({});
  const [filter, setFilter] = useState('all');
  const [bulkChecking, setBulkChecking] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/energy-control/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data?.products || []);
      setCounts(res.data?.counts || {});
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Producten konden niet worden geladen: ${detail}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const patchProduct = async (productId, fields, successMsg) => {
    setBusyIds((b) => ({ ...b, [productId]: true }));
    try {
      const res = await axios.patch(
        `${API}/admin/energy-control/products/${productId}`,
        fields,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data?.product;
      if (updated) {
        setProducts((list) => list.map((p) => (p.product_id === productId ? updated : p)));
      }
      if (successMsg) toast.success(successMsg);
      // Refresh counts after every change
      refresh();
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Opslaan mislukt: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
      return null;
    } finally {
      setBusyIds((b) => ({ ...b, [productId]: false }));
    }
  };

  const onRowPatch = (product) => async (fields) => {
    let msg = null;
    if ('status' in fields) {
      msg = `Status → ${STATUS_META[fields.status]?.label || fields.status}`;
    } else if ('visible' in fields) {
      msg = fields.visible ? 'Zichtbaar gemaakt' : 'Verborgen voor klanten';
    } else if ('featured' in fields) {
      msg = fields.featured ? 'Featured aangezet' : 'Featured uitgezet';
    }
    await patchProduct(product.product_id, fields, msg);
  };

  const filtered = useMemo(() => applyFilter(products, filter), [products, filter]);

  const runBulkAgentCheck = async () => {
    const target = filter === 'all' ? products : filtered;
    if (!target.length) {
      toast.warning('Geen producten in huidige filter om te controleren.');
      return;
    }
    if (!window.confirm(
      `Agent productcontrole starten voor ${target.length} product(en)? Dit duurt ongeveer ${Math.ceil(target.length * 4 / 3)} seconden. Er wordt niks live gezet zonder jouw goedkeuring.`
    )) return;
    setBulkChecking(true);
    try {
      const body = {};
      const res = await axios.post(
        `${API}/admin/energy-control/products/agent-check-bulk`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: target.length },
          timeout: 180000,
        },
      );
      const summary = res.data?.summary || {};
      if (summary.fail_count > 0) {
        toast.warning(`Agent-check klaar: ${summary.success_count} gelukt, ${summary.fail_count} mislukt.`);
      } else {
        toast.success(`Agent-check klaar voor ${summary.success_count} product(en).`);
      }
      setFilter('has_proposal');
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Bulk-check mislukt: ${detail}`);
    } finally {
      setBulkChecking(false);
    }
  };

  const grouped = useMemo(() => {
    const by = {};
    for (const p of filtered) {
      const cat = p.category || 'manual_start';
      if (!by[cat]) by[cat] = [];
      by[cat].push(p);
    }
    for (const cat of Object.keys(by)) {
      by[cat].sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
    }
    return by;
  }, [filtered]);

  return (
    <div data-testid="ec-products-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Productbeheer · Energy Control</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Beheer de starter-kit catalogus: prijzen, retailer-links, affiliate-links en zichtbaarheid.
            Alleen <strong>Approved + Zichtbaar</strong> producten worden aan klanten getoond.
            Ongeldige URL's vallen automatisch terug op het advies-formulier.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={runBulkAgentCheck}
            disabled={bulkChecking || loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
            data-testid="ec-products-agent-check-bulk"
          >
            {bulkChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {bulkChecking ? 'Agent controleert…' : 'Agent productcontrole (alle)'}
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted disabled:opacity-50"
            data-testid="ec-products-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Vernieuwen
          </button>
        </div>
      </div>

      {/* Approval summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <CountCard label="Pending review" value={counts.pending_review} testid="ec-products-count-pending" tone="amber" />
        <CountCard label="Approved live" value={counts.approved_live} testid="ec-products-count-approved-live" tone="emerald" />
        <CountCard label="Zonder link" value={counts.with_no_external_link} testid="ec-products-count-no-link" tone="slate" />
        <CountCard label="Needs update" value={counts.needs_update} testid="ec-products-count-needs-update" tone="orange" />
        <CountCard
          label="Rejected / Later"
          value={(counts.rejected || 0) + (counts.later || 0)}
          testid="ec-products-count-rejected-later"
          tone="rose"
        />
      </div>

      {/* Secondary count row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <CountCard label="Totaal" value={counts.total} testid="ec-products-count-total" />
        <CountCard label="Zichtbaar" value={counts.visible} testid="ec-products-count-visible" tone="emerald" />
        <CountCard label="Verborgen" value={counts.hidden} testid="ec-products-count-hidden" tone="rose" />
        <CountCard
          label="Met klant-link"
          value={(counts.total || 0) - (counts.with_no_external_link || 0)}
          testid="ec-products-count-with-link"
          tone="emerald"
        />
        <CountCard label="Approved (totaal)" value={counts.approved} testid="ec-products-count-approved" tone="emerald" />
      </div>

      {/* Filter rail */}
      <div className="flex gap-1.5 mb-5 flex-wrap" data-testid="ec-products-filter-rail">
        {FILTER_DEFS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              filter === f.id
                ? 'bg-primary text-white border-primary'
                : 'bg-white border-border text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`ec-products-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && products.length === 0 && (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Producten worden geladen…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Geen producten in deze filter.
        </div>
      )}

      <div className="space-y-6">
        {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length).map((cat) => (
          <div key={cat} data-testid={`ec-products-category-${cat}`}>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
              {CATEGORY_LABELS[cat] || cat}{' '}
              <span className="text-muted-foreground/60">({grouped[cat].length})</span>
            </h3>
            <div className="space-y-2.5">
              {grouped[cat].map((p) => (
                <ProductRow
                  key={p.product_id}
                  product={p}
                  busy={!!busyIds[p.product_id]}
                  onEdit={setEditing}
                  onPatch={onRowPatch(p)}
                  onRefresh={refresh}
                  token={token}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <ProductEditModal
        product={editing}
        open={!!editing}
        token={token}
        onClose={() => setEditing(null)}
        onSaved={(p) => {
          if (p) {
            setProducts((list) => list.map((x) => (x.product_id === p.product_id ? p : x)));
            refresh();
          }
        }}
      />
    </div>
  );
}
